/**
 * InviteUserModal – Create or edit user invitations.
 * Single responsibility: form state, validation, submit; parent handles API (Dependency Inversion).
 * Validators centralized in getValidators(editingUser) for DRY and maintainability.
 */

import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import PropTypes from 'prop-types';
import { X, Info, ChevronDown, ChevronUp } from 'lucide-react';
import { Input, Label } from '../common';
import { ACCESS_SCOPE_LABELS } from '../../constants/types/user.js';
import { fieldValidators } from '../../utils/validationSchemas';
import MultiSelectField from '../common/MultiselectorContainer';
import { useFacilitiesPageBased } from '../../hooks/useFacilitiesPageBased';
import { serviceLogger } from '../../utils/serviceLogger';

const INITIAL_FORM_DATA = {
  fullName: '',
  email: '',
  phoneNumber: '',
};

const phoneValidator = (val) => {
  if (!val || String(val).trim().length === 0) return 'Phone number is required';
  return fieldValidators.phoneNumber(val);
};

/** Single source of truth for field validators (skip email when editing). */
function getValidators(editingUser) {
  return {
    fullName: fieldValidators.fullName,
    email: editingUser ? null : fieldValidators.emailAddress,
    phoneNumber: phoneValidator,
  };
}

export const InviteUserModal = ({ isOpen, onClose, onSubmit, editingUser }) => {
  const [formData, setFormData] = useState(INITIAL_FORM_DATA);
  const [accessScope, setAccessScope] = useState('organization');
  const [selectedFacilityIds, setSelectedFacilityIds] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errors, setErrors] = useState({});
  const [touched, setTouched] = useState({});
  const [isInfoExpanded, setIsInfoExpanded] = useState(false);

  // Determine if we need to fetch facilities based on access scope
  const needsFacilities = accessScope === 'facility' || accessScope === 'multi-facility';

  // Fetch facilities for selection only when needed (facility or multi-facility scope)
  const { 
    facilities, 
    isLoading: isLoadingFacilities, 
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useFacilitiesPageBased({
    limit: 100,
    enabled: needsFacilities && isOpen, // Only fetch when modal is open and scope requires facilities
  });

  // Load all facilities pages when needed (to get complete list for selection)
  // This effect runs when the scope changes to one that needs facilities
  // Use ref to track if we're currently fetching to avoid multiple simultaneous calls
  const isFetchingRef = useRef(false);
  const lastScopeRef = useRef(accessScope);
  
  useEffect(() => {
    // Reset flag when modal closes
    if (!isOpen) {
      isFetchingRef.current = false;
      lastScopeRef.current = null;
      return;
    }

    // Reset flag when scope changes (user switched access scope)
    if (lastScopeRef.current !== accessScope) {
      isFetchingRef.current = false;
      lastScopeRef.current = accessScope;
    }

    // Only fetch if we need facilities, have next page, and aren't already fetching
    if (
      needsFacilities && 
      hasNextPage && 
      !isLoadingFacilities && 
      !isFetchingNextPage && 
      !isFetchingRef.current
    ) {
      isFetchingRef.current = true;
      fetchNextPage()
        .then(() => {
          isFetchingRef.current = false;
        })
        .catch(() => {
          isFetchingRef.current = false;
        });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [needsFacilities, isOpen, hasNextPage, isLoadingFacilities, isFetchingNextPage, accessScope]);

  const facilityOptions = useMemo(() => {
    return facilities.map((facility) => ({
      id: facility.id,
      name: facility.name,
    }));
  }, [facilities]);

  const resetForm = useCallback(() => {
    setFormData(INITIAL_FORM_DATA);
    setAccessScope('organization');
    setSelectedFacilityIds([]);
    setErrors({});
    setTouched({});
    setIsInfoExpanded(false);
  }, []);

  useEffect(() => {
    if (editingUser) {
      setFormData({
        fullName: editingUser.fullName || '',
        email: editingUser.email || '',
        phoneNumber: editingUser.phoneNumber || '',
      });
      setAccessScope(editingUser.accessScope || 'organization');
      if (editingUser.facilities && Array.isArray(editingUser.facilities)) {
        setSelectedFacilityIds(editingUser.facilities.map((f) => f.id));
      } else {
        setSelectedFacilityIds([]);
      }
    } else {
      resetForm();
    }
    setErrors({});
    setTouched({});
  }, [editingUser, isOpen, resetForm]);

  const handleAccessScopeChange = useCallback((newScope) => {
    setAccessScope(newScope);
    // Reset facility selection when switching to organization scope
    if (newScope === 'organization') {
      setSelectedFacilityIds([]);
      setErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors.facilityIds;
        return newErrors;
      });
    } else if (newScope === 'facility' || newScope === 'multi-facility') {
      // Facility Administrator: only one facility allowed; trim to first if switching from multi-facility
      if (newScope === 'facility') {
        setSelectedFacilityIds((prev) => (prev.length > 1 ? prev.slice(0, 1) : prev));
      }
      setErrors((prev) => {
        const next = { ...prev };
        delete next.facilityIds;
        return next;
      });
    }
  }, []);

  const handleInputChange = (field) => (e) => {
    const value = e.target.value;
    const updated = { ...formData, [field]: value };
    setFormData(updated);

    // Validate if field has been touched
    if (touched[field]) {
      validateField(field, value);
    }
  };

  const validateField = (field, value) => {
    const validators = getValidators(editingUser);
    const validator = validators[field];
    const error = validator ? validator(value) : null;
    setErrors((prev) => ({ ...prev, [field]: error }));
    return !error;
  };

  // Handle blur event - mark field as touched and validate
  const handleBlur = (field) => {
    setTouched((prev) => ({ ...prev, [field]: true }));
    validateField(field, formData[field]);
  };

  const validateAll = () => {
    const validators = getValidators(editingUser);
    const newTouched = {};
    const newErrors = {};
    let isValid = true;
    Object.keys(formData).forEach((field) => {
      if (editingUser && field === 'email') return;
      newTouched[field] = true;
      const validator = validators[field];
      const error = validator ? validator(formData[field]) : null;
      if (error) {
        newErrors[field] = error;
        isValid = false;
      }
    });
    setTouched(newTouched);
    setErrors(newErrors);
    return isValid;
  };

  // Validate facility selection based on access scope
  const validateFacilitySelection = () => {
    if (accessScope === 'organization') {
      // For organization scope, facilityIds must be empty
      return selectedFacilityIds.length === 0;
    } else if (accessScope === 'facility') {
      // For facility scope, must have exactly 1 facility
      return selectedFacilityIds.length === 1;
    } else if (accessScope === 'multi-facility') {
      // For multi-facility scope, must have at least 1 facility
      return selectedFacilityIds.length >= 1;
    }
    return true;
  };

  const isFormValid = () => {
    const validators = getValidators(editingUser);
    const allFieldsValid = Object.keys(formData).every((field) => {
      if (editingUser && field === 'email') return true;
      const validator = validators[field];
      if (!validator) return true;
      return !validator(formData[field]);
    });
    const fieldsToCheck = editingUser
      ? { fullName: formData.fullName, phoneNumber: formData.phoneNumber }
      : formData;
    const allFieldsFilled = Object.values(fieldsToCheck).every(
      (val) => String(val ?? '').trim() !== ''
    );
    return allFieldsValid && allFieldsFilled && validateFacilitySelection();
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validate all fields before submitting
    if (!validateAll()) {
      return;
    }

    // Validate facility selection
    if (!validateFacilitySelection()) {
      let errorMessage = '';
      if (accessScope === 'facility') {
        errorMessage = 'Please select exactly one facility';
      } else if (accessScope === 'multi-facility') {
        errorMessage = 'Please select at least one facility';
      }
      setErrors((prev) => ({ ...prev, facilityIds: errorMessage }));
      return;
    }

    try {
      setIsSubmitting(true);
      
      // Prepare request data
      const requestData = {
        fullName: formData.fullName.trim(),
        phoneNumber: formData.phoneNumber.trim(),
        accessScope,
      };

      // Email is only included when creating (not when editing, as it's immutable)
      if (!editingUser) {
        requestData.email = formData.email.trim();
      }

      // Add facilityIds only if not organization scope
      if (accessScope !== 'organization' && selectedFacilityIds.length > 0) {
        requestData.facilityIds = selectedFacilityIds;
      }

      await onSubmit(requestData);
      resetForm();
      onClose();
    } catch (error) {
      serviceLogger.error('Error submitting user:', error);
      // Set error message if provided
      if (error?.message) {
        setErrors((prev) => ({ ...prev, submit: error.message }));
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = useCallback(() => {
    resetForm();
    onClose();
  }, [resetForm, onClose]);

  if (!isOpen) return null;

  // In JS we can just iterate the keys as strings.
  const scopes = Object.keys(ACCESS_SCOPE_LABELS || {});

  return (
    <>
      {/* Backdrop */}
      <div className="fixed inset-0 bg-black/50 z-40" onClick={handleClose} />

      {/* Modal */}
      <div className="fixed inset-0 flex items-center justify-center z-50 p-4">
        <div className="bg-white rounded-lg shadow-xl w-full max-w-[740px] max-h-[90vh] overflow-y-auto">
          {/* Header */}
          <div className="flex items-center justify-between p-6 border-b border-gray-200">
            <h2 className="font-['Roboto:Bold',sans-serif] font-bold text-[24px] text-black">
              {editingUser ? 'Edit User' : 'Invite New User'}
            </h2>
            <button
              onClick={handleClose}
              className="text-gray-400 hover:text-gray-600 transition-colors"
              type="button"
            >
              <X className="size-6" />
            </button>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit} className="p-6">
            {/* Access Scope */}
            <div className="mb-6">
              <label className="font-['Roboto:Medium',sans-serif] font-medium text-[14px] text-black mb-3 block">
                Access Scope
              </label>
              <p className="font-['Roboto:Regular',sans-serif] text-[13px] text-gray-500 mb-3">
                You can change this later in user settings
              </p>

              <div className="space-y-3">
                {scopes.map((scope) => (
                  <label
                    key={scope}
                    className={`flex items-start p-4 border-2 rounded-lg cursor-pointer transition-colors ${
                      accessScope === scope
                        ? 'border-[#61b8e7] bg-[#61b8e7]/5'
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                  >
                    <input
                      type="radio"
                      name="accessScope"
                      value={scope}
                      checked={accessScope === scope}
                      onChange={(e) => handleAccessScopeChange(e.target.value)}
                      className="mt-1 mr-3"
                    />
                    <div>
                      <div className="font-['Roboto:Medium',sans-serif] font-medium text-[15px] text-black">
                        {ACCESS_SCOPE_LABELS[scope]?.title}
                      </div>
                      <div className="font-['Roboto:Regular',sans-serif] text-[13px] text-gray-500 mt-1">
                        {ACCESS_SCOPE_LABELS[scope]?.description}
                      </div>
                    </div>
                  </label>
                ))}
              </div>
            </div>

            {/* Full Name and Phone Number - Side by Side */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
              <div className="form-group">
                <Label variant="gray" isRequired={true}>
                  Full Name
                </Label>
                <Input
                  variant="primary"
                  customClass={`!max-w-full ${errors.fullName ? 'border-red-500' : ''}`}
                  placeholder="e.g., Sarah Johnson"
                  value={formData.fullName}
                  onChange={handleInputChange('fullName')}
                  onBlur={() => handleBlur('fullName')}
                />
                {errors.fullName && touched.fullName && (
                  <p className="text-red-500 text-xs mt-1">{errors.fullName}</p>
                )}
              </div>

              <div className="form-group">
                <Label variant="gray" isRequired={true}>
                  Phone Number
                </Label>
                <Input
                  variant="primary"
                  type="tel"
                  customClass={`!max-w-full ${errors.phoneNumber ? 'border-red-500' : ''}`}
                  placeholder="e.g., +1 (555) 123-4567"
                  value={formData.phoneNumber}
                  onChange={handleInputChange('phoneNumber')}
                  onBlur={() => handleBlur('phoneNumber')}
                />
                {errors.phoneNumber && touched.phoneNumber && (
                  <p className="text-red-500 text-xs mt-1">{errors.phoneNumber}</p>
                )}
              </div>
            </div>

            {/* Email Address - Full Width */}
            <div className="form-group mb-6">
              <Label variant="gray" isRequired={true}>
                Email Address
              </Label>
              <Input
                variant="primary"
                type="email"
                customClass={`!max-w-full ${errors.email ? 'border-red-500' : ''} ${
                  editingUser ? 'bg-gray-100 cursor-not-allowed' : ''
                }`}
                placeholder="e.g., sarah.johnson@company.com"
                value={formData.email}
                onChange={handleInputChange('email')}
                onBlur={() => handleBlur('email')}
                disabled={editingUser} // Email is immutable when editing
              />
              {editingUser && (
                <p className="text-gray-500 text-xs mt-1">
                  Email cannot be changed
                </p>
              )}
              {errors.email && touched.email && !editingUser && (
                <p className="text-red-500 text-xs mt-1">{errors.email}</p>
              )}
            </div>

            {/* Facility Selection - Only show for facility or multi-facility scope */}
            {(accessScope === 'facility' || accessScope === 'multi-facility') && (
              <div className="form-group mb-6">
                <Label variant="gray" isRequired={true}>
                  {accessScope === 'facility' ? 'Select Facility' : 'Select Facilities'}
                </Label>
                {isLoadingFacilities ? (
                  <div className="flex items-center gap-2 text-gray-500 text-sm py-2">
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-[#61b8e7]"></div>
                    <span>Loading facilities...</span>
                  </div>
                ) : facilityOptions.length === 0 ? (
                  <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
                    <p className="text-amber-800 text-sm">
                      No facilities available. Please create a facility first.
                    </p>
                  </div>
                ) : (
                  <>
                    <MultiSelectField
                      label={accessScope === 'facility' ? 'Select Facility' : 'Select Facilities'}
                      options={facilityOptions}
                      value={selectedFacilityIds}
                      onChange={
                        accessScope === 'facility'
                          ? (newIds) =>
                              setSelectedFacilityIds(
                                newIds.length > 1 ? [newIds[newIds.length - 1]] : newIds,
                              )
                          : setSelectedFacilityIds
                      }
                      disabled={isSubmitting || isLoadingFacilities}
                      maxVisible={3}
                    />
                    {errors.facilityIds && (
                      <p className="text-red-500 text-xs mt-1">{errors.facilityIds}</p>
                    )}
                    {accessScope === 'facility' && selectedFacilityIds.length !== 1 && (
                      <p className="text-gray-500 text-xs mt-1">
                        Please select exactly one facility
                      </p>
                    )}
                    {accessScope === 'multi-facility' && selectedFacilityIds.length === 0 && (
                      <p className="text-gray-500 text-xs mt-1">
                        Please select at least one facility
                      </p>
                    )}
                  </>
                )}
              </div>
            )}

            {/* Submit Error */}
            {errors.submit && (
              <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg">
                <p className="text-red-600 text-sm">{errors.submit}</p>
              </div>
            )}

            {/* Expandable Information - Only show for new invitations */}
            {!editingUser && (
              <div className="mb-6">
                <button
                  type="button"
                  onClick={() => setIsInfoExpanded((v) => !v)}
                  className="flex items-center gap-2 text-[#61b8e7] hover:text-[#4da3d1] transition-colors font-['Roboto:Medium',sans-serif] font-medium text-[14px]"
                >
                  <Info className="size-4" />
                  <span>How do user invitations work?</span>
                  {isInfoExpanded ? (
                    <ChevronUp className="size-4" />
                  ) : (
                    <ChevronDown className="size-4" />
                  )}
                </button>

                {isInfoExpanded && (
                  <div className="mt-3 p-4 bg-gray-50 border border-gray-200 rounded-lg">
                    <p className="font-['Roboto:Regular',sans-serif] text-[13px] text-gray-700 leading-relaxed">
                      An invitation email will be sent to the address you provide. The user will
                      receive a secure link to set up their account and choose a password.
                    </p>
                  </div>
                )}
              </div>
            )}

            {/* Action Buttons */}
            <div className="flex gap-4 justify-end pt-4">
              <button
                type="button"
                onClick={handleClose}
                disabled={isSubmitting}
                className="px-6 py-2.5 border-2 border-[#61b8e7] text-[#61b8e7] rounded-lg font-['Roboto:Bold',sans-serif] font-bold text-[14px] hover:bg-[#61b8e7]/5 transition-colors disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={isSubmitting || !isFormValid()}
                className="px-6 py-2.5 bg-[#61b8e7] text-white rounded-lg font-['Roboto:Bold',sans-serif] font-bold text-[14px] hover:bg-[#4da3d1] transition-colors disabled:opacity-50 disabled:cursor-not-allowed min-w-[120px]"
              >
                {isSubmitting ? 'Sending...' : editingUser ? 'Update' : 'Send'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </>
  );
};

InviteUserModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
  editingUser: PropTypes.shape({
    fullName: PropTypes.string,
    email: PropTypes.string,
    phoneNumber: PropTypes.string,
    accessScope: PropTypes.string,
    facilities: PropTypes.arrayOf(PropTypes.shape({ id: PropTypes.string })),
  }),
};
